import {createModel} from '@rematch/core';

import {
  ConnectedProfile,
  PhoneNumber,
  requestBindConnectedPhone,
  fetchConnectedProfiles,
  verifyConnectedPhone,
  removePhone,
  fetchPhoneNumbers,
  fetchQuestionsByModelId,
  Question,
  Review,
  fetchReviewsByModelId,
  fetchProfile,
  postQuestionReply,
  postReviewReply,
} from '~/services/hot-api/connected-profiles';
import {TListResponse} from '~/services/types';
import {ensurePlus} from '~/utils/phone';

import type {RootModel} from '.';

export interface ModalPayload {
  session: string;
  phone: string;
  actionType: 'add' | 'verify' | 'delete' | '';
}

export type ConnectedProfilesState = {
  initialPayload: any;
  profiles: ConnectedProfile[];
  profilesTotal: number;
  phoneNumbers: Array<PhoneNumber & {selected: boolean}>;
  modalPayload: ModalPayload;
  standaloneProfile: ConnectedProfile | null;
  questions: {
    modelId: ConnectedProfile['modelId'];
    total: number;
    items: Question[];
  };
  reviews: {
    modelId: ConnectedProfile['modelId'];
    total: number;
    items: Review[];
  };
};

interface LoadPhonesAndProfilesParams {
  profileId: string;
  modelId: string;
}

const defaultState: ConnectedProfilesState = {
  initialPayload: {},
  profiles: [],
  profilesTotal: 0,
  phoneNumbers: [],
  standaloneProfile: null,
  modalPayload: {
    session: '',
    phone: '',
    actionType: '',
  },
  questions: {
    modelId: '',
    total: 0,
    items: [],
  },
  reviews: {
    modelId: '',
    total: 0,
    items: [],
  },
};

export const connectedProfiles = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setPayload: (state, payload: Partial<ConnectedProfilesState>) => ({
      ...state,
      ...payload,
    }),
    setModalPayload: (state, modalPayload: ModalPayload) => ({
      ...state,
      modalPayload,
    }),
    setQuestions: (state, questions: TListResponse<Question> & {modelId: string}) => ({
      ...state,
      questions,
    }),
    setReviews: (state, reviews: TListResponse<Review> & {modelId: string}) => ({
      ...state,
      reviews,
    }),
  },
  effects: (dispatch) => ({
    loadPhonesAndProfiles: async (params?: LoadPhonesAndProfilesParams) => {
      try {
        const profilesResponse = await fetchConnectedProfiles();
        const phonesResponse = await fetchPhoneNumbers();

        const phoneNumbers = phonesResponse.data.items.map((phoneItem) => ({
          ...phoneItem,
          selected: true,
        }));

        dispatch.connectedProfiles.setPayload({
          profiles: profilesResponse.data.items,
          profilesTotal: profilesResponse.data.total,
          phoneNumbers,
        });

        if (!params) return;

        const currentModel = profilesResponse.data.items.some(
          (profile) => profile.modelId === params.modelId
        );

        // try to load
        if (!currentModel) {
          const profileResponse = await fetchProfile(params);

          dispatch.connectedProfiles.setPayload({
            standaloneProfile: profileResponse.data,
          });
        }
      } catch (error: any) {
        console.error(error);
      }
    },
    // select/unselect phone in dropdown menu
    filterSelect: async ({phone, selected}, state) => {
      const {phoneNumbers} = state.connectedProfiles;

      const newPhoneNumbers = phoneNumbers.map((phoneItem) => {
        if (phone.phoneNumber !== phoneItem.phoneNumber) {
          return phoneItem;
        }

        return {
          ...phoneItem,
          selected,
        };
      });

      dispatch.connectedProfiles.setPayload({
        phoneNumbers: newPhoneNumbers,
      });

      const requestPhoneNumbers = newPhoneNumbers
        .filter((item) => item.isVerified && item.selected)
        .map((item) => item.phoneNumber);

      const profilesResponse = await fetchConnectedProfiles({phoneNumbers: requestPhoneNumbers});

      dispatch.connectedProfiles.setPayload({
        profiles: profilesResponse.data.items,
        profilesTotal: profilesResponse.data.total,
      });
    },
    resetFilter: async (_: void, state) => {
      const {phoneNumbers} = state.connectedProfiles;

      dispatch.connectedProfiles.setPayload({
        phoneNumbers: phoneNumbers.map((phoneItem) => ({
          ...phoneItem,
          selected: true,
        })),
      });

      await dispatch.connectedProfiles.refreshPhonesAndProfiles();
    },
    loadMoreProfiles: async (_: void, state) => {
      const profiles = state.connectedProfiles.profiles;

      try {
        const profilesResponse = await fetchConnectedProfiles({
          offset: profiles.length,
        });

        dispatch.connectedProfiles.setPayload({
          profiles: [...profiles, ...profilesResponse.data.items],
          profilesTotal: profilesResponse.data.total,
        });
      } catch (error) {
        console.error(error);
      }
    },
    // Do same, but loading is not triggered
    refreshPhonesAndProfiles: async () => {
      try {
        const response = await fetchConnectedProfiles();
        dispatch.connectedProfiles.loadPhoneNumbers();

        dispatch.connectedProfiles.setPayload({
          profiles: response.data.items,
          profilesTotal: response.data.total,
        });
      } catch (error: any) {
        console.error(error);
      }
    },
    refreshProfiles: async (_: void, state) => {
      const {phoneNumbers} = state.connectedProfiles;

      const requestPhoneNumbers = phoneNumbers
        .filter((item) => item.isVerified && item.selected)
        .map((item) => item.phoneNumber);

      if (requestPhoneNumbers.length !== phoneNumbers.length) {
        const profilesResponse = await fetchConnectedProfiles({phoneNumbers: requestPhoneNumbers});

        dispatch.connectedProfiles.setPayload({
          profiles: profilesResponse.data.items,
          profilesTotal: profilesResponse.data.total,
        });
      }
    },
    loadPhoneNumbers: async (_: void, state) => {
      const {phoneNumbers} = state.connectedProfiles;
      try {
        const phonesResponse = await fetchPhoneNumbers();

        const updatedPhoneNumbers = phonesResponse.data.items.map((phoneItem) => {
          const existsPhoneItem = phoneNumbers.find(
            (item) => item.phoneNumber === phoneItem.phoneNumber
          );

          return {
            ...phoneItem,
            selected: existsPhoneItem ? existsPhoneItem.selected : true,
          };
        });
        dispatch.connectedProfiles.setPayload({
          phoneNumbers: updatedPhoneNumbers,
        });
      } catch (err) {
        console.error(err);
      }
    },
    requestBindPhone: async (phone: string) => {
      try {
        const response = await requestBindConnectedPhone(ensurePlus(phone));

        if (response.status === 204) {
          return {
            success: false,
            reason: 'duplicate',
          };
        }

        return {
          success: true,
          session: response.data.sessionId,
        };
      } catch (error) {
        return {
          success: false,
          session: 'backend',
        };
      }
    },
    verifyPhone: async ({sessionId, confirmationCode}) => {
      try {
        await verifyConnectedPhone(sessionId, confirmationCode);

        return {success: true};
      } catch (err) {
        console.error(err);
        return {success: false};
      }
    },
    deletePhone: async (phone) => {
      try {
        await removePhone(phone);
      } catch (err) {
        console.error(err);
      }
    },
    loadQuestions: async (modelId: string) => {
      try {
        const questionsResponse = await fetchQuestionsByModelId({modelId});

        dispatch.connectedProfiles.setQuestions({
          modelId,
          ...questionsResponse.data,
        });
      } catch (err) {
        console.error(err);
      }
    },
    loadMoreQuestions: async (modelId: string, state) => {
      const {questions} = state.connectedProfiles;
      const {items} = questions;

      try {
        const questionsResponse = await fetchQuestionsByModelId({modelId, offset: items.length});

        dispatch.connectedProfiles.setQuestions({
          ...state.connectedProfiles.questions,
          total: questionsResponse.data.total,
          items: [...questions.items, ...questionsResponse.data.items],
        });
      } catch (err) {
        console.error(err);
      }
    },
    loadReviews: async (modelId: string) => {
      try {
        const reviewsResponse = await fetchReviewsByModelId({modelId});

        dispatch.connectedProfiles.setReviews({
          modelId,
          ...reviewsResponse.data,
        });
      } catch (err) {
        console.error(err);
      }
    },
    loadMoreReviews: async (modelId: string, state) => {
      const {reviews} = state.connectedProfiles;
      const {items} = reviews;

      try {
        const reviewsResponse = await fetchReviewsByModelId({modelId, offset: items.length});

        dispatch.connectedProfiles.setReviews({
          ...state.connectedProfiles.reviews,
          total: reviewsResponse.data.total,
          items: [...reviews.items, ...reviewsResponse.data.items],
        });
      } catch (err) {
        console.error(err);
      }
    },
    postQuestionReply: async (params) => {
      try {
        await postQuestionReply(params);
      } catch (err) {
        console.error(err);
      }
    },
    postReviewReply: async (params) => {
      try {
        await postReviewReply(params);
      } catch (err) {
        console.error(err);
      }
    },
  }),
});
